import { Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

// Node maps for efficient tree operations
export interface NodeMaps {
  idToNodeMap: Map<string, CollectionNode>;
  pathToNodeMap: Map<string, CollectionNode>;
  nodeToPathMap: Map<string, string>;
}

/**
 * Computes maps for efficient node lookup and path-based operations
 * - idToNodeMap: Map from node ID to node
 * - pathToNodeMap: Map from path to node
 * - nodeToPathMap: Map from node ID to path
 */
export const computeNodeMaps = (rootNode: CollectionNode): NodeMaps => {
  const idToNodeMap = new Map<string, CollectionNode>();
  const pathToNodeMap = new Map<string, CollectionNode>();
  const nodeToPathMap = new Map<string, string>();

  // Traverse the tree to build the maps
  const traverse = (node: CollectionNode, parentPath = '') => {
    const path = parentPath ? `${parentPath}/${node.id}` : node.id;

    // Add to maps
    idToNodeMap.set(node.id, node);
    pathToNodeMap.set(path, node);
    nodeToPathMap.set(node.id, path);

    // Process children
    if (node.children) {
      node.children.forEach((child) => traverse(child, path));
    }
  };

  traverse(rootNode);
  return { idToNodeMap, pathToNodeMap, nodeToPathMap };
};

/**
 * Gets the parent node using the node maps
 */
export const getParentNode = (
  nodeId: string,
  nodeMaps: NodeMaps,
): CollectionNode | null => {
  const { nodeToPathMap, pathToNodeMap } = nodeMaps;

  // Get the path for this node
  const path = nodeToPathMap.get(nodeId);
  if (!path) return null;

  // Get parent path (everything before the last slash)
  const lastSlashIndex = path.lastIndexOf('/');
  if (lastSlashIndex === -1) return null; // No parent (root node)

  const parentPath = path.substring(0, lastSlashIndex);
  return pathToNodeMap.get(parentPath) || null;
};

/**
 * Gets all blobs in a folder and its descendants using path-based filtering
 */
export const getDescendantBlobs = (
  folderId: string,
  blobs: BlobType[] | undefined,
  nodeMaps: NodeMaps,
): BlobType[] => {
  if (!blobs) return [];

  const { nodeToPathMap, idToNodeMap } = nodeMaps;
  const folderPath = nodeToPathMap.get(folderId);

  if (!folderPath) return [];

  // Filter blobs that belong to this folder or any descendant folder
  return blobs.filter((blob) => {
    const blobCollectionId = blob.collection_details.id;
    const blobParentPath = nodeToPathMap.get(blobCollectionId);

    // Match if the blob's parent path starts with the target folder's path
    return (
      blobParentPath === folderPath ||
      (blobParentPath && blobParentPath.startsWith(`${folderPath}/`))
    );
  });
};

/**
 * Counts all blobs in a folder and its descendants
 */
export const countDescendantBlobs = (
  folderId: string,
  blobs: BlobType[] | undefined,
  nodeMaps: NodeMaps,
): number => {
  return getDescendantBlobs(folderId, blobs, nodeMaps).length;
};

/**
 * Expands all paths to matching files for search results
 */
export const expandMatchingPaths = (
  matchingBlobs: BlobType[],
  rootNodeId: string,
  nodeMaps: NodeMaps,
): Set<string> => {
  const { nodeToPathMap } = nodeMaps;
  const expandedFolders = new Set<string>([rootNodeId]);

  // For each matching blob, get all its ancestor folders
  matchingBlobs.forEach((blob) => {
    const collectionId = blob.collection_details.id;
    expandedFolders.add(collectionId); // Add the direct parent folder

    // Get the path for this collection
    const path = nodeToPathMap.get(collectionId);
    if (!path) return;

    // Split the path and add each ancestor ID
    const parts = path.split('/');
    for (let i = 0; i < parts.length; i += 1) {
      expandedFolders.add(parts[i]);
    }
  });

  return expandedFolders;
};

/**
 * Checks if all blobs in a folder and its descendants are selected
 */
export const checkFolderSelectionState = (
  folderId: string,
  blobs: BlobType[] | undefined,
  selectedFileIds: Set<string>,
  nodeMaps: NodeMaps,
): boolean => {
  const folderBlobs = getDescendantBlobs(folderId, blobs, nodeMaps);
  if (folderBlobs.length === 0) return false;

  // Check if all blobs in this folder and its descendants are selected
  return folderBlobs.every((blob) => selectedFileIds.has(blob.id));
};
